import { create } from "zustand";

import { PageName } from "analytics/events";

export type CreateCheckType = "QUANTITY_AVAILABLE" | "NO_QUANTITY" | null;

export type BbdCheckSessionState = {
  checkId: string | null;
  origin: PageName;
  priority: number;
  shelfNumber: string | null;
  shelfLetter: string | null;
  sku: string | null;
  // Product info
  productName: string | null;
  productImageUrl: string | null;
  productCountryOfOriginName: string | null;
  minDaysToBestBeforeDate: number | null;
  // Stock & Quantities
  productStock: number;
  expiredQuantity: number;
  tooGoodToGoQuantity: number;
  closestBbd?: Date;
  createCheckType: CreateCheckType;
};

type InitializeCheckParams = {
  checkId: string;
  origin: PageName;
  shelfNumber: string | null;
  shelfLetter: string | null;
  priority: number;
  sku: string | null;
  productName: string | null;
  productImageUrl: string | null;
  productCountryOfOriginName: string | null;
  minDaysToBestBeforeDate: number | null;
};

type DeclareQuantitiesParams = {
  expiredQuantity: number;
  tooGoodToGoQuantity: number;
};

type BbdCheckSessionActions = {
  initializeCheck: (params: InitializeCheckParams) => void;
  setProductStock: (stock: number) => void;
  declareQuantities: (params: DeclareQuantitiesParams) => void;
  setClosestBbd: (closestBbd: Date) => void;
  setCreateCheckType: (checkType: CreateCheckType) => void;
  resetCreateCheckType: () => void;
  reset: () => void;
};

export type BbdCheckSessionStore = BbdCheckSessionState & BbdCheckSessionActions;

const initialState: BbdCheckSessionState = {
  checkId: null,
  origin: PageName.ACTIVITIES_PAGE,
  priority: 0,
  shelfNumber: null,
  shelfLetter: null,
  sku: null,
  productName: null,
  productImageUrl: null,
  productCountryOfOriginName: null,
  minDaysToBestBeforeDate: null,
  productStock: 0,
  expiredQuantity: 0,
  tooGoodToGoQuantity: 0,
  closestBbd: undefined,
  createCheckType: null,
};

export const useBbdCheckStore = create<BbdCheckSessionStore>()((set) => ({
  ...initialState,
  initializeCheck: (params: InitializeCheckParams) =>
    set({
      ...initialState,
      checkId: params.checkId,
      origin: params.origin,
      shelfNumber: params.shelfNumber,
      shelfLetter: params.shelfLetter,
      priority: params.priority,
      sku: params.sku,
      productName: params.productName,
      productImageUrl: params.productImageUrl,
      productCountryOfOriginName: params.productCountryOfOriginName,
      minDaysToBestBeforeDate: params.minDaysToBestBeforeDate,
    }),
  setProductStock: (stock: number) => set({ productStock: stock }),
  declareQuantities: (params: DeclareQuantitiesParams) =>
    set({
      expiredQuantity: params.expiredQuantity,
      tooGoodToGoQuantity: params.tooGoodToGoQuantity,
    }),
  setClosestBbd: (closestBbd: Date) => set({ closestBbd }),
  setCreateCheckType: (checkType: CreateCheckType) => set({ createCheckType: checkType }),
  resetCreateCheckType: () => set({ createCheckType: null }),
  reset: () => set(initialState),
}));
